import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Skeleton,
  Stack,
  Switch,
  Table,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  ManageAccounts as ManageAccountsIcon,
  Search as SearchIcon,
  Storefront as StorefrontIcon,
} from "@mui/icons-material";
import { Link, Navigate, useParams } from "react-router-dom";

import Page from "../components/Pages/Page";
import PageError from "../components/PageError";
import PageHeader from "../components/PageHeader";
import { User } from "../common/types";
import { useIntegrationCatalog } from "../services/integrationCatalog";
import { useAuthFetch } from "../utils/useAuthFetch";
import { resolveRequiredIntegrationStates } from "./agentSkillStoreRequirements";

import bkgDataAccessIcon from "../assets/skill-icons/bkg_data_access.png";
import bmiBiomarketIntelligenceIcon from "../assets/skill-icons/bmi_biomarket_intelligence.png";
import calendarTranscriptsIcon from "../assets/skill-icons/calendar_transcripts.png";
import dashboardsIcon from "../assets/skill-icons/dashboards.png";
import lifeScienceConnectorsIcon from "../assets/skill-icons/life_science_connectors.png";
import marketAccessConnectorsIcon from "../assets/skill-icons/market_access_connectors.png";
import pdfPptxReconstructionIcon from "../assets/skill-icons/pdf_pptx_reconstruction.png";
import sowGenerationIcon from "../assets/skill-icons/sow_generation.png";
import ssiBrandSkillsIcon from "../assets/skill-icons/ssi_brand_skills.png";
import webSearchIcon from "../assets/skill-icons/web_search.png";

type JsonObject = Record<string, unknown>;
type EnableMode = "all_workspaces" | "selected_workspaces";

interface ConfigFieldSchema {
  type?: string | string[];
  label?: string;
  title?: string;
  description?: string;
  required?: boolean;
  multi?: boolean;
  options?: unknown[];
  enum?: unknown[];
  default?: unknown;
  format?: string;
}

interface ConfigField {
  name: string;
  schema: ConfigFieldSchema;
  required: boolean;
}

interface SecretDraftEntry {
  keep?: boolean;
  value?: string;
}

interface SkillOfferingMember {
  skill_key: string;
  required: boolean;
  sort_order: number;
  display_name?: string | null;
  status?: string | null;
  risk_level?: string | null;
  required_env_flag?: string | null;
  required_integrations?: string[];
}

interface SkillOffering {
  id: string;
  offering_key: string;
  display_name: string;
  description?: string | null;
  category: string;
  visibility: string;
  status: string;
  self_service: boolean;
  self_service_removable: boolean;
  risk_level: string;
  enable_mode_default: string;
  requires_configuration_default: boolean;
  config_schema: JsonObject;
  workspace_config_schema: JsonObject;
  default_config: JsonObject;
  required_env_flag?: string | null;
  source: string;
  icon_url?: string | null;
  metadata: JsonObject;
  version: number;
  members: SkillOfferingMember[];
  required_integrations?: string[];
}

interface StoreItemBase {
  id: string;
  organization_id: string;
  offering_key: string;
  status: string;
  source: string;
  enable_mode: EnableMode;
  requires_configuration: boolean;
  org_config?: JsonObject;
  org_configured_by?: string | null;
  org_configured_at?: string | null;
  notes?: string | null;
  version: number;
  created_at?: string | null;
  updated_at?: string | null;
}

interface WorkspaceSetting {
  id: string;
  organization_id: string;
  workspace_id: string;
  offering_key: string;
  available: boolean;
  org_config: JsonObject;
  org_configured_by?: string | null;
  org_configured_at?: string | null;
  version: number;
}

interface OrganizationStoreItem extends StoreItemBase {
  org_config: JsonObject;
  offering: SkillOffering;
  workspace_settings: WorkspaceSetting[];
}

interface PublicStoreItem {
  offering: SkillOffering;
  in_org_store: boolean;
  store_item?: StoreItemBase | null;
}

interface OrgWorkspaceOwner {
  id: string;
  name: string;
  email: string;
}

interface OrgWorkspace {
  id: string;
  name: string;
  status: string;
  owners: OrgWorkspaceOwner[];
}

interface SkillCardItem {
  offering: SkillOffering;
  storeItem?: OrganizationStoreItem | null;
  inOrgStore: boolean;
}

type ChipColor = "neutral" | "primary" | "success" | "warning" | "danger";

const OFFERING_ICONS: Record<string, string> = {
  bkg_data_access: bkgDataAccessIcon,
  bmi_biomarket_intelligence: bmiBiomarketIntelligenceIcon,
  calendar_transcripts: calendarTranscriptsIcon,
  dashboards: dashboardsIcon,
  life_science_connectors: lifeScienceConnectorsIcon,
  market_access_connectors: marketAccessConnectorsIcon,
  pdf_pptx_reconstruction: pdfPptxReconstructionIcon,
  sow_generation: sowGenerationIcon,
  ssi_brand_skills: ssiBrandSkillsIcon,
  web_search: webSearchIcon,
};

const normalize = (value: string) =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

const normalizeLabel = (value?: string | null) => {
  if (!value) return "Unknown";
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const isSubsequence = (needle: string, haystack: string) => {
  let index = 0;
  for (const char of haystack) {
    if (char === needle[index]) index += 1;
    if (index === needle.length) return true;
  }
  return false;
};

const matchesSearch = (offering: SkillOffering, rawQuery: string) => {
  const query = normalize(rawQuery);
  if (!query) return true;
  const haystack = normalize(
    `${offering.display_name} ${offering.description || ""}`,
  );
  return haystack.includes(query) || isSubsequence(query, haystack);
};

const getErrorMessage = (error: unknown, fallback: string) => {
  const response = (error as any)?.response;
  const detail = response?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) {
    return detail.map((entry) => entry?.msg || String(entry)).join(" ");
  }
  if (error instanceof Error) return error.message || fallback;
  return fallback;
};

const isConflict = (error: unknown) => (error as any)?.status === 409;

const isPlainObject = (value: unknown): value is JsonObject =>
  !!value && typeof value === "object" && !Array.isArray(value);

const isSecretSentinel = (value: unknown) =>
  isPlainObject(value) && value.__secret_set === true;

const hasConfigurationValues = (config?: JsonObject | null) =>
  !!config &&
  Object.values(config).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (isPlainObject(value)) return Object.keys(value).length > 0;
    return value !== undefined && value !== null && value !== "";
  });

const getFieldType = (schema: ConfigFieldSchema) => {
  const rawType = schema.type;
  const fieldType = Array.isArray(rawType)
    ? rawType.find((entry) => entry !== "null") || "string"
    : rawType || "string";
  if (schema.format === "url" || schema.format === "uri") return "url";
  return fieldType;
};

const getFieldOptions = (schema: ConfigFieldSchema) => {
  if (Array.isArray(schema.options)) return schema.options;
  if (Array.isArray(schema.enum)) return schema.enum;
  return [];
};

const getFieldLabel = (name: string, schema: ConfigFieldSchema) =>
  schema.label || schema.title || normalizeLabel(name);

const getSchemaFields = (schema: JsonObject): ConfigField[] => {
  const required = Array.isArray(schema.required)
    ? new Set(schema.required.map(String))
    : new Set<string>();
  const properties = schema.properties;
  if (isPlainObject(properties)) {
    return Object.entries(properties as Record<string, ConfigFieldSchema>).map(
      ([name, fieldSchema]) => ({
        name,
        schema: fieldSchema,
        required: required.has(name) || fieldSchema.required === true,
      }),
    );
  }

  return Object.entries(schema)
    .filter(([name, value]) => {
      if (
        ["required", "type", "title", "description", "$schema"].includes(name)
      ) {
        return false;
      }
      if (!isPlainObject(value)) return false;
      return (
        "type" in value ||
        "label" in value ||
        "title" in value ||
        "description" in value ||
        "options" in value ||
        "enum" in value
      );
    })
    .map(([name, value]) => {
      const fieldSchema = value as ConfigFieldSchema;
      return {
        name,
        schema: fieldSchema,
        required: fieldSchema.required === true,
      };
    });
};

const mergeOfferings = (
  publicStore: PublicStoreItem[],
  storeItems: OrganizationStoreItem[],
) => {
  const byKey = new Map<string, SkillCardItem>();
  publicStore.forEach((item) => {
    byKey.set(item.offering.offering_key, {
      offering: item.offering,
      storeItem: null,
      inOrgStore: item.in_org_store,
    });
  });
  storeItems.forEach((item) => {
    byKey.set(item.offering.offering_key, {
      offering: item.offering,
      storeItem: item,
      inOrgStore: item.status !== "revoked",
    });
  });
  return Array.from(byKey.values()).sort((a, b) =>
    a.offering.display_name.localeCompare(b.offering.display_name),
  );
};

const getCategoryGroupKey = (category?: string | null) => {
  const normalized = normalize(category || "");
  return normalized || "uncategorized";
};

const parseSingleValue = (
  field: ConfigField,
  rawValue: unknown,
): { value?: unknown; error?: string } => {
  const fieldType = getFieldType(field.schema);
  if (
    rawValue === undefined ||
    rawValue === null ||
    (typeof rawValue === "string" && rawValue.trim() === "")
  ) {
    return {};
  }

  if (fieldType === "number" || fieldType === "integer") {
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      return {
        error: `${getFieldLabel(field.name, field.schema)} must be a number.`,
      };
    }
    if (fieldType === "integer" && !Number.isInteger(parsed)) {
      return {
        error: `${getFieldLabel(field.name, field.schema)} must be a whole number.`,
      };
    }
    return { value: parsed };
  }

  if (fieldType === "boolean") return { value: Boolean(rawValue) };

  if (fieldType === "object" || fieldType === "array") {
    if (typeof rawValue !== "string") return { value: rawValue };
    try {
      const parsed = JSON.parse(rawValue);
      if (fieldType === "array" && !Array.isArray(parsed)) {
        return {
          error: `${getFieldLabel(field.name, field.schema)} must be a JSON array.`,
        };
      }
      if (
        fieldType === "object" &&
        (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      ) {
        return {
          error: `${getFieldLabel(field.name, field.schema)} must be a JSON object.`,
        };
      }
      return { value: parsed };
    } catch {
      return {
        error: `${getFieldLabel(field.name, field.schema)} must be valid JSON.`,
      };
    }
  }

  return { value: String(rawValue) };
};

const buildConfigDraft = (schema: JsonObject, config: JsonObject = {}) => {
  const draft: JsonObject = {};
  getSchemaFields(schema).forEach((field) => {
    const current = config[field.name];
    const fieldType = getFieldType(field.schema);
    if (field.schema.multi) {
      if (fieldType === "secret") {
        const entries = Array.isArray(current)
          ? current
          : current
            ? [current]
            : [];
        draft[field.name] = entries.map((entry) =>
          isSecretSentinel(entry)
            ? ({ keep: true, value: "" } as SecretDraftEntry)
            : ({ value: String(entry || "") } as SecretDraftEntry),
        );
      } else if (Array.isArray(current)) {
        draft[field.name] = current.map((entry) =>
          entry === undefined || entry === null ? "" : String(entry),
        );
      } else if (current !== undefined && current !== null && current !== "") {
        draft[field.name] = [String(current)];
      } else {
        draft[field.name] = [];
      }
      return;
    }

    if (fieldType === "secret") {
      draft[field.name] = isSecretSentinel(current) ? "" : current || "";
      return;
    }

    if (current !== undefined && current !== null) {
      draft[field.name] =
        fieldType === "object" || fieldType === "array"
          ? JSON.stringify(current, null, 2)
          : current;
    } else if (field.schema.default !== undefined) {
      draft[field.name] = field.schema.default;
    } else {
      draft[field.name] = fieldType === "boolean" ? false : "";
    }
  });
  return draft;
};

const serializeConfigDraft = (
  schema: JsonObject,
  draft: JsonObject,
  existingConfig: JsonObject = {},
) => {
  const errors: Record<string, string> = {};
  const payload: JsonObject = {};

  getSchemaFields(schema).forEach((field) => {
    const fieldType = getFieldType(field.schema);
    const label = getFieldLabel(field.name, field.schema);
    const draftValue = draft[field.name];
    const existingValue = existingConfig[field.name];
    const existingSecretSet = isSecretSentinel(existingValue);

    if (field.schema.multi) {
      if (fieldType === "secret") {
        const entries = Array.isArray(draftValue)
          ? (draftValue as SecretDraftEntry[])
          : [];
        const serialized = entries
          .map((entry) => {
            const value = (entry.value || "").trim();
            if (value) return value;
            if (entry.keep) return { __keep: true };
            return null;
          })
          .filter((entry) => entry !== null);
        if (field.required && serialized.length === 0) {
          errors[field.name] = `${label} is required.`;
          return;
        }
        payload[field.name] = serialized;
        return;
      }

      const values = Array.isArray(draftValue) ? draftValue : [];
      const parsedValues: unknown[] = [];
      values.forEach((entry, index) => {
        const parsed = parseSingleValue(field, entry);
        if (parsed.error) {
          errors[`${field.name}.${index}`] = parsed.error;
          return;
        }
        if (parsed.value !== undefined) parsedValues.push(parsed.value);
      });
      if (field.required && parsedValues.length === 0) {
        errors[field.name] = `${label} is required.`;
        return;
      }
      payload[field.name] = parsedValues;
      return;
    }

    if (fieldType === "secret") {
      if (draftValue === null) {
        payload[field.name] = null;
        return;
      }
      const stringValue = String(draftValue || "").trim();
      if (stringValue) {
        payload[field.name] = stringValue;
        return;
      }
      if (field.required && !existingSecretSet) {
        errors[field.name] = `${label} is required.`;
      }
      return;
    }

    if (fieldType === "boolean") {
      payload[field.name] = Boolean(draftValue);
      return;
    }

    const parsed = parseSingleValue(field, draftValue);
    if (parsed.error) {
      errors[field.name] = parsed.error;
      return;
    }
    if (field.required && parsed.value === undefined) {
      errors[field.name] = `${label} is required.`;
      return;
    }
    if (parsed.value !== undefined) payload[field.name] = parsed.value;
  });

  return { payload, errors };
};

const getStatusColor = (status: string): ChipColor => {
  if (status === "available" || status === "active") return "success";
  if (status === "deprecated") return "warning";
  if (status === "disabled" || status === "revoked") return "danger";
  return "neutral";
};

// Deterministic per-category color so a category always renders the same hue.
const CATEGORY_PALETTE: ChipColor[] = [
  "primary",
  "success",
  "warning",
  "danger",
  "neutral",
];
const getCategoryColor = (category?: string | null): ChipColor => {
  const key = getCategoryGroupKey(category);
  if (key === "uncategorized") return "neutral";
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return CATEGORY_PALETTE[Math.abs(hash) % CATEGORY_PALETTE.length];
};

const formatOwners = (owners: OrgWorkspaceOwner[]) => {
  if (owners.length === 0) return { label: "-", title: "No accepted owner" };
  const names = owners.map((owner) => owner.name || owner.email);
  const label =
    names.length <= 2
      ? names.join(", ")
      : `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
  return { label, title: names.join(", ") };
};

const upsertWorkspaceSetting = (
  settings: WorkspaceSetting[],
  nextSetting: WorkspaceSetting,
) => {
  const existingIndex = settings.findIndex(
    (setting) => setting.workspace_id === nextSetting.workspace_id,
  );
  if (existingIndex === -1) return [...settings, nextSetting];
  return settings.map((setting, index) =>
    index === existingIndex ? nextSetting : setting,
  );
};

const OfferingIcon: React.FC<{ offeringKey: string; label: string }> = ({
  offeringKey,
  label,
}) => {
  const icon = OFFERING_ICONS[offeringKey];
  if (!icon) {
    return (
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "8px",
          bgcolor: "neutral.softBg",
          display: "grid",
          placeItems: "center",
          color: "neutral.600",
        }}
      >
        <StorefrontIcon />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={icon}
      alt={label}
      sx={{
        width: 72,
        height: 72,
        borderRadius: "8px",
        objectFit: "cover",
      }}
    />
  );
};

const ConfigFieldInput: React.FC<{
  field: ConfigField;
  value: unknown;
  existingValue: unknown;
  error?: string;
  disabled?: boolean;
  onChange: (fieldName: string, value: unknown) => void;
}> = ({ field, value, existingValue, error, disabled = false, onChange }) => {
  const fieldType = getFieldType(field.schema);
  const label = getFieldLabel(field.name, field.schema);
  const helper = field.schema.description;
  const options = getFieldOptions(field.schema);

  if (field.schema.multi) {
    if (fieldType === "secret") {
      const entries = Array.isArray(value) ? (value as SecretDraftEntry[]) : [];
      return (
        <FormControl required={field.required} error={!!error}>
          <FormLabel>{label}</FormLabel>
          <Stack spacing={1}>
            {entries.map((entry, index) => (
              <Stack key={index} direction="row" spacing={1}>
                <Input
                  type="password"
                  value={entry.value || ""}
                  disabled={disabled}
                  placeholder={
                    entry.keep
                      ? "Secret set - type to replace"
                      : "Enter secret value"
                  }
                  onChange={(event) => {
                    const next = [...entries];
                    next[index] = {
                      keep: entry.keep && !event.target.value,
                      value: event.target.value,
                    };
                    onChange(field.name, next);
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  color="neutral"
                  disabled={disabled}
                  onClick={() =>
                    onChange(
                      field.name,
                      entries.filter((_, entryIndex) => entryIndex !== index),
                    )
                  }
                >
                  Remove
                </Button>
              </Stack>
            ))}
            <Button
              variant="outlined"
              color="neutral"
              disabled={disabled}
              onClick={() => onChange(field.name, [...entries, { value: "" }])}
            >
              Add another
            </Button>
          </Stack>
          {(error || helper) && (
            <FormHelperText>{error || helper}</FormHelperText>
          )}
        </FormControl>
      );
    }

    const values = Array.isArray(value) ? (value as unknown[]) : [];
    return (
      <FormControl required={field.required} error={!!error}>
        <FormLabel>{label}</FormLabel>
        <Stack spacing={1}>
          {values.map((entry, index) => (
            <Stack key={index} direction="row" spacing={1}>
              <Input
                value={String(entry || "")}
                disabled={disabled}
                onChange={(event) => {
                  const next = [...values];
                  next[index] = event.target.value;
                  onChange(field.name, next);
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                color="neutral"
                disabled={disabled}
                onClick={() =>
                  onChange(
                    field.name,
                    values.filter((_, valueIndex) => valueIndex !== index),
                  )
                }
              >
                Remove
              </Button>
            </Stack>
          ))}
          <Button
            variant="outlined"
            color="neutral"
            disabled={disabled}
            onClick={() => onChange(field.name, [...values, ""])}
          >
            Add another
          </Button>
        </Stack>
        {(error || helper) && (
          <FormHelperText>{error || helper}</FormHelperText>
        )}
      </FormControl>
    );
  }

  if (fieldType === "secret") {
    const existingSecret = isSecretSentinel(existingValue);
    const markedForClear = value === null;
    return (
      <FormControl required={field.required} error={!!error}>
        <FormLabel>{label}</FormLabel>
        <Stack spacing={1}>
          <Input
            type="password"
            value={markedForClear ? "" : String(value || "")}
            disabled={disabled || markedForClear}
            placeholder={
              existingSecret
                ? "Secret set - type to replace"
                : "Enter secret value"
            }
            onChange={(event) => onChange(field.name, event.target.value)}
          />
          {existingSecret && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="sm" variant="soft" color="neutral">
                Secret set
              </Chip>
              <Button
                size="sm"
                variant="plain"
                color={markedForClear ? "neutral" : "danger"}
                disabled={disabled}
                onClick={() => onChange(field.name, markedForClear ? "" : null)}
              >
                {markedForClear ? "Keep stored secret" : "Clear stored secret"}
              </Button>
            </Stack>
          )}
        </Stack>
        {(error || helper) && (
          <FormHelperText>{error || helper}</FormHelperText>
        )}
      </FormControl>
    );
  }

  if (options.length > 0 || fieldType === "select") {
    return (
      <FormControl required={field.required} error={!!error}>
        <FormLabel>{label}</FormLabel>
        <Select
          value={value === undefined || value === null ? "" : String(value)}
          disabled={disabled}
          onChange={(_, selectedValue) =>
            onChange(field.name, selectedValue || "")
          }
        >
          {options.map((option) => (
            <Option key={String(option)} value={String(option)}>
              {normalizeLabel(String(option))}
            </Option>
          ))}
        </Select>
        {(error || helper) && (
          <FormHelperText>{error || helper}</FormHelperText>
        )}
      </FormControl>
    );
  }

  if (fieldType === "boolean") {
    return (
      <FormControl required={field.required} error={!!error}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Switch
            checked={Boolean(value)}
            disabled={disabled}
            onChange={(event) => onChange(field.name, event.target.checked)}
          />
          <Box>
            <FormLabel>{label}</FormLabel>
            {(error || helper) && (
              <FormHelperText>{error || helper}</FormHelperText>
            )}
          </Box>
        </Stack>
      </FormControl>
    );
  }

  if (fieldType === "object" || fieldType === "array") {
    return (
      <FormControl required={field.required} error={!!error}>
        <FormLabel>{label}</FormLabel>
        <Textarea
          minRows={4}
          value={value === undefined || value === null ? "" : String(value)}
          disabled={disabled}
          placeholder={fieldType === "array" ? "[]" : "{}"}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
        {(error || helper) && (
          <FormHelperText>{error || helper}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl required={field.required} error={!!error}>
      <FormLabel>{label}</FormLabel>
      <Input
        type={
          fieldType === "number" || fieldType === "integer" ? "number" : "text"
        }
        value={value === undefined || value === null ? "" : String(value)}
        disabled={disabled}
        onChange={(event) => onChange(field.name, event.target.value)}
      />
      {(error || helper) && <FormHelperText>{error || helper}</FormHelperText>}
    </FormControl>
  );
};

const SkillCard: React.FC<{ item: SkillCardItem }> = ({ item }) => {
  const { offering, storeItem } = item;
  const enabled = item.inOrgStore && storeItem?.status === "available";
  const categoryColor = getCategoryColor(offering.category);
  const description =
    offering.description || "Agent skill for this organization.";
  return (
    <Card
      component={Link}
      to={`/organization/settings/agent-skills/${encodeURIComponent(
        offering.offering_key,
      )}`}
      variant="outlined"
      sx={{
        position: "relative",
        minHeight: 220,
        borderRadius: "8px",
        borderTop: "3px solid",
        borderTopColor: `${categoryColor}.500`,
        textDecoration: "none",
        transition: "border-color 120ms ease, transform 120ms ease",
        "&:hover": {
          borderColor: "primary.outlinedBorder",
          borderTopColor: `${categoryColor}.500`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {enabled && (
        <Box
          aria-label="Enabled in this organization"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "success.600",
          }}
        >
          <CheckCircleIcon fontSize="small" />
        </Box>
      )}
      <CardContent>
        <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
          <OfferingIcon
            offeringKey={offering.offering_key}
            label={offering.display_name}
          />
          <Box sx={{ minWidth: 0, width: "100%" }}>
            <Typography
              level="title-md"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {offering.display_name}
            </Typography>
            <Tooltip
              title={description}
              variant="soft"
              placement="top"
              sx={{ maxWidth: 320 }}
            >
              <Typography
                level="body-sm"
                color="neutral"
                sx={{
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  minHeight: "40px",
                }}
              >
                {description}
              </Typography>
            </Tooltip>
          </Box>
          <Chip size="sm" variant="soft" color={categoryColor}>
            {offering.category
              ? normalizeLabel(offering.category)
              : "Uncategorized"}
          </Chip>
        </Stack>
      </CardContent>
    </Card>
  );
};

const SkillCardSkeletons: React.FC = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: 2,
    }}
  >
    {Array.from({ length: 6 }, (_, index) => (
      <Card
        key={index}
        variant="outlined"
        sx={{ minHeight: 220, borderRadius: "8px" }}
      >
        <CardContent>
          <Stack spacing={2} alignItems="center">
            <Skeleton variant="rectangular" width={72} height={72} />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="80%" />
          </Stack>
        </CardContent>
      </Card>
    ))}
  </Box>
);

const AgentSkillStore: React.FC = () => {
  const authFetch = useAuthFetch();
  const { offering_key: offeringKeyParam } = useParams();
  const [user, setUser] = useState<User | null>(null);
  const [skills, setSkills] = useState<SkillCardItem[]>([]);
  const [workspaces, setWorkspaces] = useState<OrgWorkspace[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configDraft, setConfigDraft] = useState<JsonObject>({});
  const [configErrors, setConfigErrors] = useState<Record<string, string>>({});
  const [configError, setConfigError] = useState<string | null>(null);
  const [configConflict, setConfigConflict] = useState(false);
  const [configMessage, setConfigMessage] = useState<string | null>(null);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [workspaceMessage, setWorkspaceMessage] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [workspaceConfigTarget, setWorkspaceConfigTarget] =
    useState<OrgWorkspace | null>(null);
  const [workspaceConfigDraft, setWorkspaceConfigDraft] = useState<JsonObject>(
    {},
  );
  const [workspaceConfigErrors, setWorkspaceConfigErrors] = useState<
    Record<string, string>
  >({});
  const [workspaceConfigError, setWorkspaceConfigError] = useState<
    string | null
  >(null);
  const [workspaceConfigConflict, setWorkspaceConfigConflict] = useState(false);
  const [workspaceConfigMessage, setWorkspaceConfigMessage] = useState<
    string | null
  >(null);
  const [copySourceWorkspaceId, setCopySourceWorkspaceId] = useState("");
  const [copyIncludeSecrets, setCopyIncludeSecrets] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingWorkspaceConfig, setSavingWorkspaceConfig] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [savingStore, setSavingStore] = useState(false);
  const [copyingConfig, setCopyingConfig] = useState(false);
  const [removingStoreItem, setRemovingStoreItem] = useState(false);
  const [lockedAssignments, setLockedAssignments] = useState(false);
  const decodedOfferingKey = offeringKeyParam
    ? decodeURIComponent(offeringKeyParam)
    : null;
  const {
    entries: integrationCatalogEntries,
    loading: integrationCatalogLoading,
    error: integrationCatalogError,
    usingStub: integrationCatalogUsingStub,
  } = useIntegrationCatalog(user?.organization_id || null, null, true);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = (await authFetch(
        "/api/user/read",
        "fetch user",
      )) as User;
      setUser(userData);
      if (
        userData.role_in_org !== "admin" ||
        userData.status !== "verified" ||
        !userData.organization_id
      ) {
        setSkills([]);
        setWorkspaces([]);
        return;
      }
      const orgId = userData.organization_id;
      const publicStore = (await authFetch(
        `/api/organization/${orgId}/ida/skills/store`,
        "fetch agent skill catalog",
      )) as PublicStoreItem[];
      const storeItems = (await authFetch(
        `/api/organization/${orgId}/ida/skills/store-items`,
        "fetch organization agent skills",
      )) as OrganizationStoreItem[];
      setSkills(mergeOfferings(publicStore, storeItems));

      if (offeringKeyParam) {
        const orgWorkspaces = (await authFetch(
          `/api/organization/${orgId}/workspaces`,
          "fetch organization workspaces",
        )) as OrgWorkspace[];
        setWorkspaces(orgWorkspaces);
      }
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Couldn't load the skill catalog."));
    } finally {
      setLoading(false);
    }
  }, [authFetch, offeringKeyParam]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredSkills = useMemo(
    () => skills.filter((item) => matchesSearch(item.offering, search)),
    [search, skills],
  );

  const selectedItem = useMemo(() => {
    if (!decodedOfferingKey) return null;
    return (
      skills.find(
        (item) => item.offering.offering_key === decodedOfferingKey,
      ) || null
    );
  }, [decodedOfferingKey, skills]);

  const selectedStoreItem = selectedItem?.storeItem || null;
  const selectedOffering = selectedItem?.offering || null;
  const enabled = selectedStoreItem?.status === "available";
  const integrationCatalogByKey = useMemo(
    () =>
      new Map(
        integrationCatalogEntries.map((entry) => [entry.key, entry] as const),
      ),
    [integrationCatalogEntries],
  );
  const requiredIntegrationStates = useMemo(
    () =>
      resolveRequiredIntegrationStates(
        selectedOffering?.required_integrations || [],
        integrationCatalogEntries,
      ),
    [integrationCatalogEntries, selectedOffering],
  );
  const missingRequiredIntegrations = useMemo(
    () => requiredIntegrationStates.filter((item) => !item.enabled),
    [requiredIntegrationStates],
  );
  const deltekConnectionRequired = (
    selectedOffering?.required_integrations || []
  ).includes("deltek");
  const deltekConnectionStatus =
    integrationCatalogByKey.get("deltek")?.user_connection_status;
  const deltekConnectionMissing =
    deltekConnectionRequired &&
    (integrationCatalogLoading ||
      integrationCatalogUsingStub ||
      deltekConnectionStatus !== "connected");
  const canEnableSkill =
    !integrationCatalogLoading &&
    !integrationCatalogUsingStub &&
    missingRequiredIntegrations.length === 0;
  const autoEnabledIntegrations = useMemo(
    () =>
      missingRequiredIntegrations.filter((item) => {
        const entry = integrationCatalogByKey.get(item.key);
        return entry?.archetype === "no_config";
      }),
    [integrationCatalogByKey, missingRequiredIntegrations],
  );
  // Whether the offering is ALREADY in the org store. This -- not the presence
  // of a loaded store-item object -- is the correct discriminator for add vs
  // status: an already-present offering must never take the add (POST) path,
  // which 409s ("Offering is already in the organization store").
  const selectedItemInOrgStore = selectedItem?.inOrgStore ?? false;
  const orgConfigFields = selectedOffering
    ? getSchemaFields(selectedOffering.config_schema)
    : [];
  const workspaceConfigFields = selectedOffering
    ? getSchemaFields(selectedOffering.workspace_config_schema)
    : [];
  const hasWorkspaceConfig = workspaceConfigFields.length > 0;
  const selectedWorkspaceIds = useMemo(
    () =>
      new Set(
        selectedStoreItem?.workspace_settings
          .filter((setting) => setting.available)
          .map((setting) => setting.workspace_id) || [],
      ),
    [selectedStoreItem],
  );
  const workspaceConfigSetting = useMemo(() => {
    if (!selectedStoreItem || !workspaceConfigTarget) return null;
    return (
      selectedStoreItem.workspace_settings.find(
        (setting) => setting.workspace_id === workspaceConfigTarget.id,
      ) || null
    );
  }, [selectedStoreItem, workspaceConfigTarget]);
  const copySourceOptions = useMemo(() => {
    if (!selectedStoreItem || !workspaceConfigTarget) return [];
    return selectedStoreItem.workspace_settings
      .filter(
        (setting) =>
          setting.workspace_id !== workspaceConfigTarget.id &&
          hasConfigurationValues(setting.org_config),
      )
      .map((setting) => {
        const workspace = workspaces.find(
          (candidate) => candidate.id === setting.workspace_id,
        );
        return {
          workspaceId: setting.workspace_id,
          label: workspace?.name || setting.workspace_id,
        };
      });
  }, [selectedStoreItem, workspaceConfigTarget, workspaces]);

  useEffect(() => {
    if (!selectedOffering || !selectedStoreItem) {
      setConfigDraft({});
      setConfigErrors({});
      return;
    }
    setConfigDraft(
      buildConfigDraft(
        selectedOffering.config_schema,
        selectedStoreItem.org_config || {},
      ),
    );
    setConfigErrors({});
    setConfigError(null);
    setConfigMessage(null);
    setConfigConflict(false);
  }, [selectedOffering, selectedStoreItem]);

  const updateSkillStoreItem = useCallback(
    (updatedItem: OrganizationStoreItem) => {
      setSkills((currentSkills) =>
        currentSkills.map((skill) =>
          skill.offering.offering_key === updatedItem.offering_key
            ? {
                offering: updatedItem.offering,
                storeItem: updatedItem,
                inOrgStore: updatedItem.status !== "revoked",
              }
            : skill,
        ),
      );
    },
    [],
  );

  const removeSkillStoreItem = useCallback((offeringKey: string) => {
    setSkills((currentSkills) =>
      currentSkills.map((skill) =>
        skill.offering.offering_key === offeringKey
          ? { ...skill, storeItem: null, inOrgStore: false }
          : skill,
      ),
    );
  }, []);

  const updateWorkspaceSetting = useCallback(
    (nextSetting: WorkspaceSetting) => {
      setSkills((currentSkills) =>
        currentSkills.map((skill) => {
          if (skill.offering.offering_key !== nextSetting.offering_key) {
            return skill;
          }
          if (!skill.storeItem) return skill;
          return {
            ...skill,
            storeItem: {
              ...skill.storeItem,
              workspace_settings: upsertWorkspaceSetting(
                skill.storeItem.workspace_settings,
                nextSetting,
              ),
            },
          };
        }),
      );
    },
    [],
  );

  const refreshStoreItems = useCallback(async () => {
    if (!user?.organization_id) return [] as OrganizationStoreItem[];
    const storeItems = (await authFetch(
      `/api/organization/${user.organization_id}/ida/skills/store-items`,
      "fetch organization agent skills",
    )) as OrganizationStoreItem[];
    setSkills((currentSkills) => {
      const byKey = new Map<string, SkillCardItem>();
      currentSkills.forEach((skill) => {
        byKey.set(skill.offering.offering_key, skill);
      });
      storeItems.forEach((item) => {
        byKey.set(item.offering.offering_key, {
          offering: item.offering,
          storeItem: item,
          inOrgStore: item.status !== "revoked",
        });
      });
      return Array.from(byKey.values()).sort((a, b) =>
        a.offering.display_name.localeCompare(b.offering.display_name),
      );
    });
    return storeItems;
  }, [authFetch, user?.organization_id]);

  const handleConfigChange = (fieldName: string, value: unknown) => {
    setConfigDraft((currentDraft) => ({ ...currentDraft, [fieldName]: value }));
    setConfigErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const handleWorkspaceConfigChange = (fieldName: string, value: unknown) => {
    setWorkspaceConfigDraft((currentDraft) => ({
      ...currentDraft,
      [fieldName]: value,
    }));
    setWorkspaceConfigErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const handleOpenWorkspaceConfig = (workspace: OrgWorkspace) => {
    if (!selectedOffering || !selectedStoreItem) return;
    const setting =
      selectedStoreItem.workspace_settings.find(
        (workspaceSetting) => workspaceSetting.workspace_id === workspace.id,
      ) || null;
    const sourceOptions = selectedStoreItem.workspace_settings.filter(
      (workspaceSetting) =>
        workspaceSetting.workspace_id !== workspace.id &&
        hasConfigurationValues(workspaceSetting.org_config),
    );
    setWorkspaceConfigTarget(workspace);
    setWorkspaceConfigDraft(
      buildConfigDraft(
        selectedOffering.workspace_config_schema,
        setting?.org_config || {},
      ),
    );
    setWorkspaceConfigErrors({});
    setWorkspaceConfigError(null);
    setWorkspaceConfigConflict(false);
    setWorkspaceConfigMessage(null);
    setCopySourceWorkspaceId(sourceOptions[0]?.workspace_id || "");
    setCopyIncludeSecrets(false);
  };

  const handleCloseWorkspaceConfig = () => {
    setWorkspaceConfigTarget(null);
    setWorkspaceConfigDraft({});
    setWorkspaceConfigErrors({});
    setWorkspaceConfigError(null);
    setWorkspaceConfigConflict(false);
    setWorkspaceConfigMessage(null);
    setCopySourceWorkspaceId("");
    setCopyIncludeSecrets(false);
  };

  const submitSkillEnableRequest = useCallback(
    async (mode: "add" | "status") => {
      if (!user?.organization_id || !selectedOffering) return null;

      if (mode === "add") {
        return (await authFetch(
          `/api/organization/${user.organization_id}/ida/skills/store-items`,
          "fetch agent skill store add",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              offering_key: selectedOffering.offering_key,
            }),
          },
        )) as OrganizationStoreItem;
      }

      if (!selectedStoreItem) return null;

      return (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/status`,
        "fetch agent skill status save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: selectedStoreItem.version,
            status: "available",
          }),
        },
      )) as OrganizationStoreItem;
    },
    [authFetch, selectedOffering, selectedStoreItem, user?.organization_id],
  );

  const attemptSkillEnable = useCallback(
    async (mode: "add" | "status") => {
      setSavingStore(true);
      setStatusError(null);
      try {
        const response = await submitSkillEnableRequest(mode);
        if (!response) return;
        updateSkillStoreItem(response);
      } catch (enableError) {
        setStatusError(
          getErrorMessage(
            enableError,
            "Couldn't enable this skill for the organization.",
          ),
        );
      } finally {
        setSavingStore(false);
      }
    },
    [submitSkillEnableRequest, updateSkillStoreItem],
  );

  const handleAddToStore = async () => {
    if (!user?.organization_id || !selectedOffering) return;
    await attemptSkillEnable("add");
  };

  // Route by org-store membership so an existing item always uses status PUT.
  const handleEnableSkill = async () => {
    if (!user?.organization_id || !selectedOffering) return;

    if (selectedItemInOrgStore) {
      // Already in the org store -> status (PUT) path; never add (POST).
      if (!selectedStoreItem) {
        // Defensive: membership is known but the store-item/version isn't
        // loaded (e.g. an org-managed offering flagged in_org_store without a
        // manageable store-item row). Refresh once so a retry has the version,
        // and avoid blindly POST-adding an existing offering.
        await refreshStoreItems();
        setStatusError(
          "Reloaded this skill's store state. Click Enable again to apply.",
        );
        return;
      }
      await handleStatusChange("available");
      return;
    }

    // Genuinely not in the org store yet -> add (POST).
    await handleAddToStore();
  };

  const handleStatusChange = async (nextStatus: "available" | "disabled") => {
    if (!user?.organization_id || !selectedOffering || !selectedStoreItem)
      return;
    if (nextStatus === "available") {
      await attemptSkillEnable("status");
      return;
    }
    setSavingStore(true);
    setStatusError(null);
    try {
      const updatedItem = (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/status`,
        "fetch agent skill status save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: selectedStoreItem.version,
            status: nextStatus,
          }),
        },
      )) as OrganizationStoreItem;
      updateSkillStoreItem(updatedItem);
    } catch (statusChangeError) {
      setStatusError(
        isConflict(statusChangeError)
          ? "This skill changed while you were editing. Reload before changing status."
          : getErrorMessage(
              statusChangeError,
              "Couldn't update this skill status.",
            ),
      );
    } finally {
      setSavingStore(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!user?.organization_id || !selectedOffering || !selectedStoreItem)
      return;
    const { payload, errors } = serializeConfigDraft(
      selectedOffering.config_schema,
      configDraft,
      selectedStoreItem.org_config || {},
    );
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }

    setSavingConfig(true);
    setConfigError(null);
    setConfigMessage(null);
    setConfigConflict(false);
    try {
      const updatedItem = (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/config`,
        "fetch agent skill config save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: selectedStoreItem.version,
            org_config: payload,
          }),
        },
      )) as OrganizationStoreItem;
      updateSkillStoreItem(updatedItem);
      setConfigMessage("Configuration saved.");
    } catch (saveError) {
      setConfigConflict(isConflict(saveError));
      setConfigError(
        isConflict(saveError)
          ? "This skill changed while you were editing. Reload before saving."
          : getErrorMessage(saveError, "Couldn't save configuration."),
      );
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveWorkspaceConfig = async () => {
    if (
      !user?.organization_id ||
      !selectedOffering ||
      !selectedStoreItem ||
      !workspaceConfigTarget
    ) {
      return;
    }
    const { payload, errors } = serializeConfigDraft(
      selectedOffering.workspace_config_schema,
      workspaceConfigDraft,
      workspaceConfigSetting?.org_config || {},
    );
    if (Object.keys(errors).length > 0) {
      setWorkspaceConfigErrors(errors);
      return;
    }

    setSavingWorkspaceConfig(true);
    setWorkspaceConfigError(null);
    setWorkspaceConfigMessage(null);
    setWorkspaceConfigConflict(false);
    try {
      const updatedSetting = (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/workspaces/${encodeURIComponent(workspaceConfigTarget.id)}/config`,
        "fetch agent skill workspace config save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: workspaceConfigSetting?.version ?? 0,
            org_config: payload,
          }),
        },
      )) as WorkspaceSetting;
      updateWorkspaceSetting(updatedSetting);
      setWorkspaceConfigDraft(
        buildConfigDraft(
          selectedOffering.workspace_config_schema,
          updatedSetting.org_config || {},
        ),
      );
      setWorkspaceConfigMessage("Workspace configuration saved.");
    } catch (saveError) {
      setWorkspaceConfigConflict(isConflict(saveError));
      setWorkspaceConfigError(
        isConflict(saveError)
          ? "This workspace configuration changed while you were editing. Reload before saving."
          : getErrorMessage(
              saveError,
              "Couldn't save workspace configuration.",
            ),
      );
    } finally {
      setSavingWorkspaceConfig(false);
    }
  };

  const handleCopyWorkspaceConfig = async () => {
    if (
      !user?.organization_id ||
      !selectedOffering ||
      !workspaceConfigTarget ||
      !copySourceWorkspaceId
    ) {
      return;
    }
    setCopyingConfig(true);
    setWorkspaceConfigError(null);
    setWorkspaceConfigMessage(null);
    setWorkspaceConfigConflict(false);
    try {
      await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/config-copy`,
        "fetch agent skill config copy",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            source: {
              offering_key: selectedOffering.offering_key,
              workspace_id: copySourceWorkspaceId,
            },
            targets: [
              {
                offering_key: selectedOffering.offering_key,
                workspace_id: workspaceConfigTarget.id,
                expected_version: workspaceConfigSetting?.version ?? 0,
              },
            ],
            include_secret_refs: copyIncludeSecrets,
          }),
        },
      );
      const storeItems = await refreshStoreItems();
      const refreshedItem = storeItems.find(
        (item) => item.offering_key === selectedOffering.offering_key,
      );
      const refreshedSetting = refreshedItem?.workspace_settings.find(
        (setting) => setting.workspace_id === workspaceConfigTarget.id,
      );
      setWorkspaceConfigDraft(
        buildConfigDraft(
          selectedOffering.workspace_config_schema,
          refreshedSetting?.org_config || {},
        ),
      );
      setWorkspaceConfigMessage("Workspace configuration copied.");
    } catch (copyError) {
      setWorkspaceConfigConflict(isConflict(copyError));
      setWorkspaceConfigError(
        isConflict(copyError)
          ? "This workspace configuration changed while you were copying. Reload before trying again."
          : getErrorMessage(
              copyError,
              "Couldn't copy workspace configuration.",
            ),
      );
    } finally {
      setCopyingConfig(false);
    }
  };

  const setAvailabilityMode = async (nextMode: EnableMode) => {
    if (!user?.organization_id || !selectedOffering || !selectedStoreItem)
      return null;
    setSavingAvailability(true);
    setWorkspaceError(null);
    setWorkspaceMessage(null);
    try {
      const updatedItem = (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/availability`,
        "fetch agent skill availability save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: selectedStoreItem.version,
            enable_mode: nextMode,
          }),
        },
      )) as OrganizationStoreItem;
      updateSkillStoreItem(updatedItem);
      setWorkspaceMessage("Availability updated.");
      return updatedItem;
    } catch (availabilityError) {
      setWorkspaceError(
        isConflict(availabilityError)
          ? "This skill changed while you were editing. Reload before changing availability."
          : getErrorMessage(availabilityError, "Couldn't update availability."),
      );
      return null;
    } finally {
      setSavingAvailability(false);
    }
  };

  const saveWorkspaceSelection = async (
    workspaceIds: string[],
    itemOverride?: OrganizationStoreItem | null,
    lockedOverride?: boolean,
  ) => {
    const activeItem = itemOverride || selectedStoreItem;
    if (!user?.organization_id || !selectedOffering || !activeItem) return;
    setSavingAvailability(true);
    setWorkspaceError(null);
    setWorkspaceMessage(null);
    try {
      const updatedItem = (await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}/workspaces`,
        "fetch agent skill workspace assignment save",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expected_version: activeItem.version,
            workspace_ids: workspaceIds,
            locked: lockedOverride ?? lockedAssignments,
          }),
        },
      )) as OrganizationStoreItem;
      updateSkillStoreItem(updatedItem);
      setWorkspaceMessage("Workspace access updated.");
    } catch (selectionError) {
      setWorkspaceError(
        isConflict(selectionError)
          ? "This skill changed while you were editing. Reload before changing workspaces."
          : getErrorMessage(
              selectionError,
              "Couldn't update workspace access.",
            ),
      );
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleModeChange = async (nextMode: EnableMode) => {
    if (!selectedStoreItem || selectedStoreItem.enable_mode === nextMode)
      return;
    await setAvailabilityMode(nextMode);
  };

  const handleWorkspaceToggle = async (
    workspaceId: string,
    checked: boolean,
  ) => {
    if (!selectedStoreItem) return;
    let activeItem = selectedStoreItem;
    let nextIds = Array.from(selectedWorkspaceIds);
    if (activeItem.enable_mode !== "selected_workspaces") {
      const updatedItem = await setAvailabilityMode("selected_workspaces");
      if (!updatedItem) return;
      activeItem = updatedItem;
      const allWorkspaceIds = workspaces.map((workspace) => workspace.id);
      nextIds = checked
        ? [workspaceId]
        : allWorkspaceIds.filter((id) => id !== workspaceId);
    } else {
      const currentIds = new Set(nextIds);
      if (checked) {
        currentIds.add(workspaceId);
      } else {
        currentIds.delete(workspaceId);
      }
      nextIds = Array.from(currentIds);
    }
    await saveWorkspaceSelection(nextIds, activeItem);
  };

  const handleLockedChange = async (checked: boolean) => {
    setLockedAssignments(checked);
    if (
      !selectedStoreItem ||
      selectedStoreItem.enable_mode !== "selected_workspaces"
    ) {
      return;
    }
    await saveWorkspaceSelection(
      Array.from(selectedWorkspaceIds),
      selectedStoreItem,
      checked,
    );
  };

  const handleRemoveFromStore = async () => {
    if (!user?.organization_id || !selectedOffering || !selectedStoreItem)
      return;
    const confirmed = window.confirm(
      "Remove this skill and all workspace assignments? This cannot be undone.",
    );
    if (!confirmed) return;

    setRemovingStoreItem(true);
    setStatusError(null);
    try {
      await authFetch(
        `/api/organization/${user.organization_id}/ida/skills/store-items/${encodeURIComponent(
          selectedOffering.offering_key,
        )}`,
        "fetch agent skill store remove",
        { method: "DELETE" },
      );
      removeSkillStoreItem(selectedOffering.offering_key);
    } catch (removeError) {
      setStatusError(
        getErrorMessage(removeError, "Couldn't remove this skill."),
      );
    } finally {
      setRemovingStoreItem(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h1">Agent Skill Store</Typography>
        </PageHeader>
        <SkillCardSkeletons />
      </Page>
    );
  }

  if (!user) {
    return <PageError>User not found</PageError>;
  }

  if (
    user.role_in_org !== "admin" ||
    user.status !== "verified" ||
    !user.organization_id
  ) {
    return <Navigate to="/" replace />;
  }

  if (offeringKeyParam) {
    if (!selectedItem || !selectedOffering) {
      return (
        <Page>
          <Stack spacing={3}>
            <Button
              component={Link}
              to="/organization/settings/agent-skills"
              variant="plain"
              color="neutral"
              startDecorator={<ArrowBackIcon />}
              sx={{ alignSelf: "flex-start" }}
            >
              Back to Agent Skill Store
            </Button>
            {error ? (
              <Alert
                color="danger"
                variant="soft"
                endDecorator={<Button onClick={loadData}>Retry</Button>}
              >
                {error}
              </Alert>
            ) : (
              <Alert color="warning" variant="soft">
                Skill not found in this environment.
              </Alert>
            )}
          </Stack>
        </Page>
      );
    }

    return (
      <Page>
        <Stack spacing={3}>
          <Button
            component={Link}
            to="/organization/settings/agent-skills"
            variant="plain"
            color="neutral"
            startDecorator={<ArrowBackIcon />}
            sx={{ alignSelf: "flex-start" }}
          >
            Back to Agent Skill Store
          </Button>

          {error && (
            <Alert
              color="danger"
              variant="soft"
              endDecorator={<Button onClick={loadData}>Retry</Button>}
            >
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                justifyContent="space-between"
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                  <OfferingIcon
                    offeringKey={selectedOffering.offering_key}
                    label={selectedOffering.display_name}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="h1">
                      {selectedOffering.display_name}
                    </Typography>
                    <Typography level="body-md" color="neutral" sx={{ mt: 1 }}>
                      {selectedOffering.description ||
                        "Agent skill for this organization."}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 2, flexWrap: "wrap" }}
                    >
                      <Chip size="sm" variant="soft" color="neutral">
                        {normalizeLabel(selectedOffering.category)}
                      </Chip>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={getStatusColor(selectedOffering.status)}
                      >
                        {normalizeLabel(selectedOffering.status)}
                      </Chip>
                      {selectedStoreItem && (
                        <Chip
                          size="sm"
                          variant="soft"
                          color={getStatusColor(selectedStoreItem.status)}
                        >
                          Store: {normalizeLabel(selectedStoreItem.status)}
                        </Chip>
                      )}
                    </Stack>
                  </Box>
                </Stack>

                {(enabled || (canEnableSkill && !deltekConnectionMissing)) && (
                  <FormControl
                    orientation="horizontal"
                    sx={{ alignItems: "center" }}
                  >
                    <FormLabel sx={{ m: 0 }}>
                      {enabled && (!canEnableSkill || deltekConnectionMissing)
                        ? "Disable"
                        : "Enable"}
                    </FormLabel>
                    <Switch
                      checked={enabled}
                      disabled={savingStore}
                      onChange={(event) => {
                        if (event.target.checked) {
                          if (canEnableSkill && !deltekConnectionMissing) {
                            void handleEnableSkill();
                          }
                          return;
                        }
                        if (selectedStoreItem) {
                          void handleStatusChange("disabled");
                        }
                      }}
                    />
                  </FormControl>
                )}
              </Stack>
              {statusError && (
                <Alert color="danger" variant="soft" sx={{ mt: 2 }}>
                  {statusError}
                </Alert>
              )}
              {deltekConnectionMissing && !integrationCatalogLoading && (
                <Alert
                  color="warning"
                  variant="soft"
                  sx={{ mt: 2 }}
                  endDecorator={
                    workspaces[0] ? (
                      <Button
                        component={Link}
                        to={`/workspace/${encodeURIComponent(workspaces[0].id)}/settings#integrations`}
                        size="sm"
                        variant="soft"
                        color="warning"
                      >
                        {deltekConnectionStatus === "reconnect"
                          ? "Reconnect your Deltek account"
                          : "Connect your Deltek account"}
                      </Button>
                    ) : undefined
                  }
                >
                  {deltekConnectionStatus === "reconnect"
                    ? "Reconnect your Deltek account from a workspace before enabling this skill."
                    : "Connect your Deltek account from a workspace before enabling this skill."}
                </Alert>
              )}
            </CardContent>
          </Card>

          {selectedOffering.required_integrations &&
            selectedOffering.required_integrations.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography level="h2">Required integrations</Typography>
                      <Typography level="body-sm" color="neutral">
                        This skill depends on these organization integrations
                        before it can be enabled.
                      </Typography>
                    </Box>

                    {integrationCatalogError && integrationCatalogUsingStub && (
                      <Alert color="warning" variant="soft">
                        Integration status is unavailable right now. The backend
                        will still validate dependencies when you enable this
                        skill.
                      </Alert>
                    )}

                    {!integrationCatalogLoading &&
                      !integrationCatalogUsingStub &&
                      missingRequiredIntegrations.map((integration) => (
                        <Typography key={integration.key} level="body-sm">
                          Ask your organisation admin to set up{" "}
                          {integration.displayName}
                        </Typography>
                      ))}

                    {!integrationCatalogLoading &&
                      !integrationCatalogUsingStub &&
                      autoEnabledIntegrations.length > 0 && (
                        <Alert color="success" variant="soft">
                          Enabling this skill will also activate{" "}
                          {autoEnabledIntegrations
                            .map((item) => item.displayName)
                            .join(", ")}{" "}
                          automatically.
                        </Alert>
                      )}

                    {integrationCatalogLoading ? (
                      <Typography level="body-sm" color="neutral">
                        Loading integration status...
                      </Typography>
                    ) : (
                      <Sheet variant="outlined" sx={{ borderRadius: "8px" }}>
                        <Table hoverRow>
                          <thead>
                            <tr>
                              <th>Integration</th>
                              <th>Category</th>
                              <th>Status</th>
                              <th style={{ textAlign: "right" }}>Manage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requiredIntegrationStates.map((integration) => (
                              <tr key={integration.key}>
                                <td>
                                  <Stack spacing={0.25}>
                                    <Typography level="body-sm">
                                      {integration.displayName}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                      {integration.key}
                                    </Typography>
                                  </Stack>
                                </td>
                                <td>
                                  {integration.category
                                    ? normalizeLabel(integration.category)
                                    : "Uncategorized"}
                                </td>
                                <td>
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    color={
                                      integration.enabled
                                        ? "success"
                                        : "warning"
                                    }
                                  >
                                    {integration.enabled
                                      ? "Enabled"
                                      : "Disabled"}
                                  </Chip>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  <Button
                                    component={Link}
                                    to="/admin/integrations"
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                  >
                                    Configure
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Sheet>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

          {!enabled || !selectedStoreItem ? (
            <Alert color="neutral" variant="soft">
              Enable this skill to configure organization settings and workspace
              access.
            </Alert>
          ) : (
            <>
              {orgConfigFields.length > 0 && (
                <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Typography level="h2">Configuration</Typography>
                        <Typography level="body-sm" color="neutral">
                          These settings are shared across every workspace that
                          can use this skill.
                        </Typography>
                      </Box>

                      {configError && (
                        <Alert
                          color="danger"
                          variant="soft"
                          endDecorator={
                            configConflict ? (
                              <Button onClick={loadData}>Reload</Button>
                            ) : undefined
                          }
                        >
                          {configError}
                        </Alert>
                      )}
                      {configMessage && (
                        <Alert color="success" variant="soft">
                          {configMessage}
                        </Alert>
                      )}

                      <Stack spacing={2}>
                        {orgConfigFields.map((field) => (
                          <ConfigFieldInput
                            key={field.name}
                            field={field}
                            value={configDraft[field.name]}
                            existingValue={
                              selectedStoreItem.org_config?.[field.name]
                            }
                            error={configErrors[field.name]}
                            disabled={savingConfig}
                            onChange={handleConfigChange}
                          />
                        ))}
                      </Stack>

                      <Button
                        variant="solid"
                        loading={savingConfig}
                        onClick={handleSaveConfig}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Save configuration
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography level="h2">Workspaces</Typography>
                      <Typography level="body-sm" color="neutral">
                        Choose which workspaces can use this skill.
                      </Typography>
                    </Box>

                    {workspaceError && (
                      <Alert
                        color="danger"
                        variant="soft"
                        endDecorator={
                          <Button onClick={loadData}>Reload</Button>
                        }
                      >
                        {workspaceError}
                      </Alert>
                    )}
                    {workspaceMessage && (
                      <Alert color="success" variant="soft">
                        {workspaceMessage}
                      </Alert>
                    )}
                    <RadioGroup
                      value={selectedStoreItem.enable_mode}
                      onChange={(event) =>
                        void handleModeChange(event.target.value as EnableMode)
                      }
                    >
                      <Radio
                        value="all_workspaces"
                        label="All workspaces"
                        disabled={savingAvailability}
                      />
                      <Radio
                        value="selected_workspaces"
                        label="Selected workspaces"
                        disabled={savingAvailability}
                      />
                    </RadioGroup>

                    <Checkbox
                      checked={lockedAssignments}
                      disabled={
                        savingAvailability ||
                        selectedStoreItem.enable_mode !== "selected_workspaces"
                      }
                      onChange={(event) =>
                        void handleLockedChange(event.target.checked)
                      }
                      label="Lock workspace access"
                    />

                    {workspaces.length === 0 ? (
                      <Alert color="neutral" variant="soft">
                        No workspaces are available in this organization.
                      </Alert>
                    ) : (
                      <Sheet
                        variant="outlined"
                        sx={{ borderRadius: "8px", overflow: "auto" }}
                      >
                        <Table
                          hoverRow
                          stickyHeader
                          sx={{
                            minWidth: hasWorkspaceConfig ? 720 : 560,
                            "& th": { whiteSpace: "nowrap" },
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={{ width: 64 }}>Use</th>
                              <th>Workspace</th>
                              <th>Owner</th>
                              {hasWorkspaceConfig && <th>Workspace config</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {workspaces.map((workspace) => {
                              const available =
                                selectedStoreItem.enable_mode ===
                                  "all_workspaces" ||
                                selectedWorkspaceIds.has(workspace.id);
                              const owners = formatOwners(workspace.owners);
                              const setting =
                                selectedStoreItem.workspace_settings.find(
                                  (workspaceSetting) =>
                                    workspaceSetting.workspace_id ===
                                    workspace.id,
                                );
                              const hasSavedWorkspaceConfig =
                                hasConfigurationValues(setting?.org_config);
                              return (
                                <tr key={workspace.id}>
                                  <td>
                                    <Checkbox
                                      checked={available}
                                      disabled={savingAvailability}
                                      onChange={(event) =>
                                        void handleWorkspaceToggle(
                                          workspace.id,
                                          event.target.checked,
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Stack spacing={0.25}>
                                      <Typography level="body-sm">
                                        {workspace.name}
                                      </Typography>
                                      <Typography
                                        level="body-xs"
                                        color="neutral"
                                      >
                                        {normalizeLabel(workspace.status)}
                                      </Typography>
                                    </Stack>
                                  </td>
                                  <td>
                                    <Tooltip title={owners.title}>
                                      <Typography level="body-sm">
                                        {owners.label}
                                      </Typography>
                                    </Tooltip>
                                  </td>
                                  {hasWorkspaceConfig && (
                                    <td>
                                      <Button
                                        size="sm"
                                        variant="outlined"
                                        color="neutral"
                                        disabled={!available}
                                        onClick={() =>
                                          handleOpenWorkspaceConfig(workspace)
                                        }
                                      >
                                        {hasSavedWorkspaceConfig
                                          ? "Edit configuration"
                                          : "Configure"}
                                      </Button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </Sheet>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {selectedOffering.self_service_removable && (
                <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography level="h2">Danger zone</Typography>
                        <Typography level="body-sm" color="neutral">
                          Remove this skill and all workspace assignments from
                          the organization.
                        </Typography>
                      </Box>
                      <Button
                        color="danger"
                        variant="outlined"
                        startDecorator={<DeleteIcon />}
                        loading={removingStoreItem}
                        onClick={handleRemoveFromStore}
                      >
                        Remove from store
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {workspaceConfigTarget && (
                <Modal
                  open
                  onClose={handleCloseWorkspaceConfig}
                  aria-labelledby="workspace-skill-config-title"
                >
                  <ModalDialog
                    variant="outlined"
                    sx={{
                      width: { xs: "calc(100vw - 32px)", md: 680 },
                      maxWidth: "100%",
                      borderRadius: "8px",
                    }}
                  >
                    <ModalClose />
                    <DialogTitle id="workspace-skill-config-title">
                      Workspace config: {workspaceConfigTarget.name}
                    </DialogTitle>
                    <Stack spacing={2}>
                      <Typography level="body-sm" color="neutral">
                        {selectedOffering.display_name} workspace-specific
                        settings.
                      </Typography>

                      {workspaceConfigError && (
                        <Alert
                          color="danger"
                          variant="soft"
                          endDecorator={
                            workspaceConfigConflict ? (
                              <Button onClick={loadData}>Reload</Button>
                            ) : undefined
                          }
                        >
                          {workspaceConfigError}
                        </Alert>
                      )}
                      {workspaceConfigMessage && (
                        <Alert color="success" variant="soft">
                          {workspaceConfigMessage}
                        </Alert>
                      )}

                      <Stack spacing={2}>
                        {workspaceConfigFields.map((field) => (
                          <ConfigFieldInput
                            key={field.name}
                            field={field}
                            value={workspaceConfigDraft[field.name]}
                            existingValue={
                              workspaceConfigSetting?.org_config?.[field.name]
                            }
                            error={workspaceConfigErrors[field.name]}
                            disabled={savingWorkspaceConfig || copyingConfig}
                            onChange={handleWorkspaceConfigChange}
                          />
                        ))}
                      </Stack>

                      <Divider />

                      <Box>
                        <Typography level="title-md">Copy from</Typography>
                        <Typography
                          level="body-sm"
                          color="neutral"
                          sx={{ mt: 0.5 }}
                        >
                          Copy this skill's workspace config from another
                          workspace. The copy is applied immediately; you can
                          still edit fields here afterward and save again.
                        </Typography>
                      </Box>

                      {copySourceOptions.length === 0 ? (
                        <Alert color="neutral" variant="soft">
                          No other workspace has saved configuration for this
                          skill yet.
                        </Alert>
                      ) : (
                        <Stack spacing={1.5}>
                          <FormControl>
                            <FormLabel>Source workspace</FormLabel>
                            <Select
                              value={copySourceWorkspaceId}
                              disabled={copyingConfig || savingWorkspaceConfig}
                              onChange={(_, value) =>
                                setCopySourceWorkspaceId(value || "")
                              }
                            >
                              {copySourceOptions.map((option) => (
                                <Option
                                  key={option.workspaceId}
                                  value={option.workspaceId}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </Select>
                          </FormControl>
                          <Checkbox
                            checked={copyIncludeSecrets}
                            disabled={copyingConfig || savingWorkspaceConfig}
                            onChange={(event) =>
                              setCopyIncludeSecrets(event.target.checked)
                            }
                            label="Include secrets"
                          />
                          <Button
                            variant="outlined"
                            color="neutral"
                            loading={copyingConfig}
                            disabled={
                              !copySourceWorkspaceId || savingWorkspaceConfig
                            }
                            onClick={handleCopyWorkspaceConfig}
                            sx={{ alignSelf: "flex-start" }}
                          >
                            Copy from{" "}
                            {copySourceOptions.find(
                              (option) =>
                                option.workspaceId === copySourceWorkspaceId,
                            )?.label || "workspace"}
                          </Button>
                        </Stack>
                      )}

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          variant="plain"
                          color="neutral"
                          onClick={handleCloseWorkspaceConfig}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="solid"
                          loading={savingWorkspaceConfig}
                          disabled={copyingConfig}
                          onClick={handleSaveWorkspaceConfig}
                        >
                          Save configuration
                        </Button>
                      </Stack>
                    </Stack>
                  </ModalDialog>
                </Modal>
              )}
            </>
          )}
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Box>
          <Typography level="h1">Agent Skill Store</Typography>
          <Typography level="body-sm" color="neutral">
            Manage the agent skills available across your organization.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {user.is_metalmind_admin && (
            <Button
              component={Link}
              to="/admin/metalmind"
              variant="outlined"
              color="neutral"
              startDecorator={<ManageAccountsIcon />}
            >
              Manage catalog
            </Button>
          )}
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search skills"
            startDecorator={<SearchIcon />}
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          />
        </Stack>
      </PageHeader>

      <Stack spacing={2}>
        {error && (
          <Alert
            color="danger"
            variant="soft"
            endDecorator={<Button onClick={loadData}>Retry</Button>}
          >
            Couldn't load the skill catalog.
          </Alert>
        )}

        {!error && skills.length === 0 && (
          <Alert color="neutral" variant="soft">
            No skills available in this environment.
          </Alert>
        )}

        {!error && skills.length > 0 && filteredSkills.length === 0 && (
          <Alert color="neutral" variant="soft">
            No skills match that search.
          </Alert>
        )}

        {filteredSkills.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 2,
            }}
          >
            {filteredSkills.map((item) => (
              <SkillCard key={item.offering.offering_key} item={item} />
            ))}
          </Box>
        )}
      </Stack>
    </Page>
  );
};

export default AgentSkillStore;
